import { MAIN_QUESTS_GROUP } from "@/constants";
import { usePassportProvider } from "@/context";
import { useQuest } from "@/context/QuestProvider";
import { useGetWalletGems } from "@/hooks/api";
import { useOnboardingQuests } from "@/hooks/quest/useOnboardingQuests";
import { notifyError } from "@/utils/monitoring";
import {
  Body,
  Box,
  Heading,
  Icon,
  MenuItem,
  ProgressBar,
  Stack,
  hFlex,
  useWindowSizeStore,
} from "@biom3/react";
import { useOnboardingQuestData } from "@hooks/quest/useOnboardingQuestData";
import type { Content } from "@prismicio/client";
import { BREAKPOINTS } from "@ui-kit";
import type { WishlistProps } from "@ui-kit";
import type { QuestGroup, QuestGroupState } from "@ui-kit/QuestList";
import { RiveFollowButton } from "@ui-kit/RiveFollowButton";
import NextLink from "next/link";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Quests as QuestsDefault } from "../Quests";
import SteamDeckGiveawayButton from "../SteamDeckGiveawayButton";

type QuestsPanelProps = {
  wishlistProps: WishlistProps;
  showHeader?: boolean;
  game: Content.GameDocument;
  questsGroups: QuestGroup[] | null;
  twitchDropsGameUid?: string;
};

type SideQuestsProps = {
  game: Content.GameDocument;
  wishlistProps: WishlistProps;
  questGroupState: QuestGroupState[];
  questsGroups: QuestGroup[];
};

const gameContentHeading = (game: Content.GameDocument) => {
  switch (game.uid) {
    case "norma-in-metaland":
      return "Real rewards for real gamers";
    case "ravenquest":
      return (
        <>
          Play{" "}
          <NextLink
            href={{ pathname: "https://ravenquest.io" }}
            style={{ color: "inherit" }}
          >
            RavenQuest:
          </NextLink>{" "}
          Treasures Below
        </>
      );
    default:
      return "";
  }
};

const BODY_CONTENT: Record<string, string> = {
  "norma-in-metaland": "",
  ravenquest: "Check daily for more rewards",
  fates: "Forge your Legend in a World of Heroes",
  "syndicate-of-vigilantes-game":
    "Follow us, stay tuned and be the first to access our Early Access!",
};

const DEFAULT_BODY = "Real Rewards for Real Gamers";

export function renderBodyContent(game: Content.GameDocument): string {
  return BODY_CONTENT[game.uid] ?? DEFAULT_BODY;
}

const getEndDate = (game: Content.GameDocument) => {
  switch (game.uid) {
    case "treeverse":
      return new Date("2025-05-18T12:00:00Z");
    case "norma-in-metaland":
      return new Date("2025-05-11T24:00:00Z");
    default:
      return new Date("2025-05-11T24:00:00Z");
  }
};

const getDaysLeft = (game: Content.GameDocument) => {
  return Math.ceil(
    (new Date(getEndDate(game)).getTime() - new Date().getTime()) /
      (1000 * 60 * 60 * 24),
  );
};

const getDaysAgo = (game: Content.GameDocument) => {
  return Math.floor(
    (new Date().getTime() - new Date(getEndDate(game)).getTime()) /
      (1000 * 60 * 60 * 24),
  );
};

const getFollowers = (game: Content.GameDocument) => {
  switch (game.uid) {
    case "treeverse":
      return "25,233";
    case "norma-in-metaland":
      return "44,731";
    default:
      return 0;
  }
};

const QuestsPanelHeader = ({
  game,
  isWishlisted,
}: {
  game: Content.GameDocument;
  isWishlisted: boolean;
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
      }}
    >
      <Heading size="small" sx={{ color: "base.color.text.heading.primary" }}>
        {isWishlisted
          ? (game.data.following_quest_header ??
            `Keep supporting ${game.data.name}`)
          : (game.data.not_following_quest_header ??
            `Get behind ${game.data.name}`)}
      </Heading>
    </Box>
  );
};

const SideQuests = ({
  game,
  wishlistProps,
  questGroupState,
  questsGroups,
}: SideQuestsProps) => {
  const firstSideQuest = useMemo(
    () => questGroupState.find((g) => g.questGroup !== MAIN_QUESTS_GROUP),
    [questGroupState],
  );

  return (
    <>
      {!wishlistProps.isWishlisted ? (
        <MenuItem emphasized size="small">
          <MenuItem.Label
            size="medium"
            sx={{
              position: "center",
              marginLeft: "3px",
              marginTop: "15px",
            }}
          >
            {game.uid === "immutable-play-onboarding"
              ? "Complete onboarding quests for rewards"
              : "Learn about the game"}
          </MenuItem.Label>
          <MenuItem.Caption sx={{ width: "100%", marginTop: "5px" }}>
            <ProgressBar
              value={0}
              variant="determinate"
              motionProfile="gentle"
              sx={{
                pb: "base.spacing.x2",
                "& .barContainer": {
                  bg: "base.color.translucent.standard.500",
                },
              }}
            >
              <ProgressBar.Label
                text={
                  <Body
                    size="xxSmall"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      color: "base.color.text.body.secondary",
                    }}
                  >
                    <Box
                      sx={{
                        mr: "base.spacing.x1",
                        color: "currentColor",
                      }}
                    />
                    {firstSideQuest?.totalGemsGranted
                      ? `0/${firstSideQuest?.totalGemsGranted}`
                      : null}
                  </Body>
                }
              />
              <ProgressBar.Caption
                sx={{
                  ...hFlex,
                  gap: "base.spacing.x1",
                  alignItems: "center",
                }}
              />
            </ProgressBar>
          </MenuItem.Caption>
        </MenuItem>
      ) : (
        <QuestsDefault
          show={true}
          game={game}
          wishlistProps={wishlistProps}
          useVariantBExperiment={true}
          questsGroups={questsGroups}
        />
      )}
    </>
  );
};

export const QuestsPanel = ({
  wishlistProps,
  showHeader = true,
  game,
  questsGroups,
  twitchDropsGameUid,
}: QuestsPanelProps) => {
  const [trackProgressSelected, setTrackProgressSelected] =
    useState<boolean>(true);
  const { walletAddress } = usePassportProvider();
  const router = useRouter();

  const isPointsTemplate = false;

  const {
    questsGroups: staticQuestGroups,
    hasOnboardingQuests: hasOnboardingQuestsFromData,
  } = useOnboardingQuestData(game.uid, false);
  const { state: questGroupState } = useQuest((store) => store);
  const { walletGems, mutate: mutateGetWalletGems } = useGetWalletGems(
    walletAddress || null,
    false,
  );

  const hasCMSQuests = !!(questsGroups && questsGroups.length > 0);
  const hasOnboardingQuests = hasCMSQuests ? true : hasOnboardingQuestsFromData;
  const quests = hasCMSQuests ? questsGroups : staticQuestGroups;
  const { state: height } = useWindowSizeStore((store) => store.height);
  const renderAvatar = Boolean(height && height > BREAKPOINTS.large);

  const isWishlisted = useMemo(
    () => wishlistProps.isWishlisted,
    [wishlistProps.isWishlisted],
  );

  const getRules = () => {
    return null;
  };

  const hasQuests = questGroupState.length > 0;

  const revalidateGemsAmount = useCallback(
    () =>
      walletGems &&
      mutateGetWalletGems({ ...walletGems }, { revalidate: true }),
    [walletGems, mutateGetWalletGems],
  );

  const asyncCompleteHandler = useRef<
    (() => Promise<Error | undefined>) | undefined
  >(undefined);

  // trigger the quest initialisation
  useOnboardingQuests(game, quests, isWishlisted);
  const [isLoading, setIsLoading] = useState(false);
  const [showFollowButton, setShowFollowButton] = useState(!isWishlisted);

  const handleFollowButtonClick = async () => {
    setIsLoading(true);

    try {
      await wishlistProps.onWishlistClick("GameSpecs");
    } catch (err) {
      notifyError(err, "riveFollowButton");
    } finally {
      setIsLoading(false);
    }
  };

  const handleTwitchDropsClick = useCallback(() => {
    router.push(`/twitch-drops/${twitchDropsGameUid}`);
  }, [router, twitchDropsGameUid]);

  // Delay the follow button from showing until the rive animation is complete
  useEffect(() => {
    if (!isWishlisted) {
      setShowFollowButton(true);
      return;
    }
    const timer = setTimeout(() => {
      setShowFollowButton(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, [isWishlisted]);

  return (
    <Stack
      sx={{
        position: "sticky",
        top: "base.spacing.x20",
        background: "base.color.translucent.inverse.500",
        borderRadius: "base.borderRadius.x8",
        padding: "base.spacing.x6",
        gap: "base.spacing.x4",
        maxHeight: "calc(100vh - 13rem)",
        overflowY: "scroll",
      }}
    >
      {showHeader ? (
        <QuestsPanelHeader isWishlisted={isWishlisted} game={game} />
      ) : null}

      <>
        <Heading
          size="medium"
          sx={{
            textAlign: "center",
            color: "base.color.text.heading.secondary",
          }}
        >
          {gameContentHeading(game)}
        </Heading>
        {twitchDropsGameUid && (
          <Box sx={{ position: "absolute", right: "base.spacing.x4" }}>
            <Icon
              icon="Twitch"
              sx={{
                w: "base.icon.size.400",
                fill: "#a855f7",
                animation: "pulse 2s infinite",
                cursor: "pointer",
                "@keyframes pulse": {
                  "0%": { transform: "scale(1)" },
                  "50%": { transform: "scale(1.2)" },
                  "100%": { transform: "scale(1)" },
                },
              }}
              onClick={handleTwitchDropsClick}
            />
          </Box>
        )}
        <Body
          size="medium"
          sx={{
            textAlign: "center",
            color: "base.color.text.body.secondary",
          }}
        >
          {renderBodyContent(game)}
        </Body>
      </>

      {game.uid === "immutable-play-onboarding" ? (
        <SteamDeckGiveawayButton
          isWishlisted={isWishlisted}
          wishlistProps={wishlistProps}
        />
      ) : (
        showFollowButton && (
          <RiveFollowButton
            isWishlisted={isWishlisted}
            isLoading={isLoading}
            onClick={handleFollowButtonClick}
            game={game}
          />
        )
      )}

      {hasQuests && (
        <SideQuests
          game={game}
          wishlistProps={wishlistProps}
          questGroupState={questGroupState}
          questsGroups={quests}
        />
      )}
    </Stack>
  );
};
